import type { CSSProperties, ReactNode } from "react";
import dayjs from "dayjs";
import { Check, Radio } from "lucide-react";
import { Badge, Card, EmptyState, ErrorState, LoadingState } from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { Link } from "react-router-dom";
import type { Competition } from "../../lib/queries/competitions";
import {
  COMPETITION_STATUS_LABELS,
  ENTRY_PHASE_LABELS,
  countdownIfNear,
  formatCompetitionDatesShort,
} from "../../lib/competitionState";
import { SEASON_PHASE_LABELS, SEASON_PHASE_VARIANT } from "../../lib/seasonPhase";
import type { SeasonPhase } from "../../lib/queries/memberships";
import { todayPercent } from "./todayPosition";
import styles from "./SeasonTrack.module.css";

// The season axis. docs/mockups/DESIGN.md §6 makes this the dashboard's
// opening element instead of a row of KPI tiles: every competition of the
// season in order, with „teď" marked.
//
// Three visual states, and only one of them is a colour. „Odjeté" fills the
// dot, „finále" thickens it, and the competition that is *running* is the
// single red thing on the page. Adding a second colour here breaks the
// signal, not just the style.

export interface SeasonTrackProps {
  seasonCode: string;
  phase?: SeasonPhase;
  competitions: Competition[];
  isLoading?: boolean;
  error?: unknown;
  /** Where a stop links, per competition. Return undefined for no link. */
  hrefFor?: (c: Competition) => string | undefined;
  /** Rendered in the empty state — e.g. „Založit soutěž" for an admin. */
  emptyAction?: ReactNode;
  emptyDescription?: string;
}

type StopKind = "done" | "now" | "final" | "future";

function stopKind(c: Competition): StopKind {
  if (c.status === "running") return "now";
  if (c.status === "finished" || c.status === "archived") return "done";
  if (c.type === "mcr_final") return "final";
  return "future";
}

// stateLine is the one-line answer to „what is happening with this one".
// A published competition is described by its entry phase (that is what a
// team acts on); anything else by its stored status.
function stateLine(c: Competition, now: dayjs.Dayjs): string {
  if (c.status !== "published") return COMPETITION_STATUS_LABELS[c.status];
  if (c.entry_phase === "open") {
    if (!c.entry_deadline) return ENTRY_PHASE_LABELS.open;
    const until = `Přihlašování do ${c.entry_deadline.format("D. M.")}`;
    const left = countdownIfNear(c.entry_deadline, now);
    return left ? `${until} (${left})` : until;
  }
  return ENTRY_PHASE_LABELS[c.entry_phase];
}

function entriesLine(c: Competition): string | null {
  if (!c.entry_count) return null;
  return `${c.entry_count} ${pluralizeCzech(c.entry_count, ["přihláška", "přihlášky", "přihlášek"])}`;
}

export function SeasonTrack({
  seasonCode,
  phase,
  competitions,
  isLoading,
  error,
  hrefFor,
  emptyAction,
  emptyDescription,
}: SeasonTrackProps) {
  const now = dayjs();
  const sorted = [...competitions].sort((a, b) => a.date.localeCompare(b.date));
  const todayAt = todayPercent(sorted, now);

  return (
    <Card>
      <Card.Content>
        <div className={styles.head}>
          <h2 className={styles.title}>
            Sezóna {seasonCode}
            {phase && <Badge variant={SEASON_PHASE_VARIANT[phase]}>{SEASON_PHASE_LABELS[phase]}</Badge>}
          </h2>
        </div>

        {isLoading ? (
          <LoadingState label="Načítám kalendář sezóny…" />
        ) : error ? (
          <ErrorState error={error} title="Nepodařilo se načíst kalendář sezóny." />
        ) : sorted.length === 0 ? (
          <EmptyState
            title="Sezóna zatím nemá žádné soutěže"
            description={emptyDescription}
            action={emptyAction}
          />
        ) : (
          <div className={styles.trackWrap}>
            {todayAt !== null && (
              <div
                className={styles.today}
                // At the very start or end of the rail a centred label would hang
                // off the card, so it tucks inward and the tick stays put.
                style={{
                  left: `${todayAt}%`,
                  transform:
                    todayAt === 0
                      ? "translateX(0)"
                      : todayAt === 100
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                  "--tick-x": todayAt === 0 ? "1px" : todayAt === 100 ? "100%" : "50%",
                } as CSSProperties}
                aria-label="Dnes"
              >
                <span className={styles.todayLabel}>dnes</span>
                <span className={styles.todayTick} />
              </div>
            )}
            <ol className={styles.track}>
            {sorted.map((c) => {
              const kind = stopKind(c);
              const href = hrefFor?.(c);
              const entries = entriesLine(c);
              const body = (
                <>
                  {/* The year is dropped (every stop shares it), the last day
                      is not: a two-day competition reads „7.–8. 3.". */}
                  <div className={styles.date}>{formatCompetitionDatesShort(c)}</div>
                  <div className={styles.dot} />
                  <div className={styles.name}>{c.name}</div>
                  {c.venue && (
                    <div className={styles.place} title={c.venue}>
                      {c.venue}
                    </div>
                  )}
                  <div className={styles.state}>
                    {kind === "done" && <Check size={13} aria-hidden />}
                    {kind === "now" && <Radio size={13} aria-hidden />}
                    <span>
                      {stateLine(c, now)}
                      {entries ? ` · ${entries}` : ""}
                    </span>
                  </div>
                </>
              );
              return (
                <li
                  key={c.id}
                  className={[
                    styles.stop,
                    kind === "done" ? styles.isDone : "",
                    kind === "now" ? styles.isNow : "",
                    kind === "final" ? styles.isFinal : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {href ? (
                    <Link to={href} className={styles.stopBody}>
                      {body}
                    </Link>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
            </ol>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
